import { SOCIAL_LINKS } from "@/lib/site-config";

const BASE_URL = "https://instanodes.io";
const ORG_ID = `${BASE_URL}/#organization`;

const organization = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Instanodes",
  url: BASE_URL,
  description:
    "Managed blockchain infrastructure: RPC Endpoints, rollups, validator nodes, and appchains as a service. SOC 2 Type II certified, 99.95% uptime SLA.",
  logo: {
    "@type": "ImageObject",
    "@id": `${BASE_URL}/#logo`,
    url: `${BASE_URL}/images/logos/instanodes.svg`,
  },
  email: "support@instanodes.io",
  telephone: "+918968617335",
  foundingDate: "2022",
  areaServed: "Worldwide",
  knowsAbout: [
    "Blockchain Infrastructure",
    "RPC Nodes",
    "Validator Nodes",
    "Rollup as a Service",
    "Appchain Development",
    "Web3 Development",
    "Ethereum Infrastructure",
    "Solana Infrastructure",
  ],
  // Official identity profiles only — these let generative engines and
  // knowledge graphs consolidate "Instanodes" into one verified entity, which
  // is a legitimate reputation signal (no third-party review data is invented).
  sameAs: [
    SOCIAL_LINKS.twitter,
    SOCIAL_LINKS.linkedin,
    SOCIAL_LINKS.github,
    SOCIAL_LINKS.medium,
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      telephone: "+918968617335",
      availableLanguage: "English",
      email: "support@instanodes.io",
      url: `${BASE_URL}/contact-us`,
    },
  ],
  hasCredential: {
    "@type": "EducationalOccupationalCredential",
    credentialCategory: "certification",
    name: "SOC 2 Type II",
    recognizedBy: {
      "@type": "Organization",
      name: "AICPA",
    },
  },
};

const organizationRef = { "@id": ORG_ID };

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    ...organization,
  };
}

export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    name: "Instanodes",
    url: BASE_URL,
    publisher: organizationRef,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${BASE_URL}/blogs?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function articleSchema(post: {
  title: string;
  description: string;
  slug: string;
  date: string;
  dateModified?: string;
  category: string;
  image?: string;
  authorName?: string;
  authorUrl?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    url: `${BASE_URL}/blogs/${post.slug}`,
    datePublished: post.date,
    dateModified: post.dateModified ?? post.date,
    image: post.image ?? `${BASE_URL}/images/og-default.png`,
    // Person-typed author with @id pointing at /about-us improves E-E-A-T signal
    // and lets generative engines consolidate the entity across posts.
    author: {
      "@type": "Person",
      "@id": post.authorUrl ?? `${BASE_URL}/about-us#author-instanodes-team`,
      name: post.authorName ?? "Instanodes Team",
      url: post.authorUrl ?? `${BASE_URL}/about-us`,
      worksFor: organizationRef,
    },
    publisher: organizationRef,
    articleSection: post.category,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${BASE_URL}/blogs/${post.slug}`,
    },
  };
}

export function howToSchema(params: {
  name: string;
  description: string;
  steps: { name: string; text: string }[];
  totalTime?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: params.name,
    description: params.description,
    ...(params.totalTime ? { totalTime: params.totalTime } : {}),
    step: params.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function serviceSchema(
  name: string,
  description: string,
  url: string,
  // Optional pricing. Passed for the pricing page so the plans are described as
  // a Service (which we provide) rather than a Product — Product snippets
  // trigger "missing review / aggregateRating" warnings and self-serving
  // ratings would violate Google's structured-data policy.
  offers?: Record<string, unknown>
) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url,
    serviceType: "Blockchain Infrastructure",
    areaServed: "Worldwide",
    provider: organizationRef,
    ...(offers ? { offers } : {}),
  };
}

export function faqSchema(items: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}
